// Inward along a cut run is to the right of travel.
import { pointAlong, pointInPolygon, polylineLength } from './goreGeometry.js';

export const PALETTE = {
	meat: 0x980000,
	meatDark: 0x600000,
	meatPit: 0x480000,
	meatBright: 0xd80000,
	meatSheen: 0xe84040,
	outline: 0x2a0000,
	fat: 0xf8e0d0,
	bone: 0xf4ead8,
	boneLine: 0x4a3838,
	marrow: 0xa83040,
	fibre: 0xf0d0f8,
	fibreDark: 0xc8a8c8,
	gut: 0xf0c8a8,
	gutDark: 0xc07060,
	gutSheen: 0xfae2c8,
	lung: 0xf0a8b8,
	lungDark: 0xc07080,
	liver: 0x6a1020,
};

export const inward = (p, depth) => ({x: p.x - p.ty * depth, y: p.y + p.tx * depth});

export const coverage = (art, poly) => {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const v of poly) {
		minX = Math.min(minX, v.x); minY = Math.min(minY, v.y);
		maxX = Math.max(maxX, v.x); maxY = Math.max(maxY, v.y);
	}
	let count = 0;
	for (let y = Math.floor(minY) + 1; y < maxY; y += 2) {
		for (let x = Math.floor(minX) + 1; x < maxX; x += 2) {
			if (art.alphaAt(x, y) > 0.5 && pointInPolygon(poly, x, y)) count += 4;
		}
	}
	return count;
};

export const fibres = (shapes, run, random) => {
	const roots = Math.round(polylineLength(run) / 6);
	for (let i = 0; i < roots; i++) {
		const p = pointAlong(run, 0.06 + random() * 0.88);
		const out = Math.atan2(-p.tx, p.ty);
		const strands = random() < 0.7 ? 1 : 2;
		for (let s = 0; s < strands; s++) {
			const angle = out + (random() * 2 - 1) * 0.6;
			const reach = 2.2 + random() * 4.5;
			const bend = (random() * 2 - 1) * 1.1;
			const sx = p.x - Math.cos(angle) * 0.8;
			const sy = p.y - Math.sin(angle) * 0.8;
			const ex = p.x + Math.cos(angle) * reach;
			const ey = p.y + Math.sin(angle) * reach;
			const cx = (sx + ex) / 2 - Math.sin(angle) * bend;
			const cy = (sy + ey) / 2 + Math.cos(angle) * bend;
			const points = [];
			for (let k = 0; k <= 3; k++) {
				const t = k / 3;
				points.push({
					x: (1 - t) * (1 - t) * sx + 2 * (1 - t) * t * cx + t * t * ex,
					y: (1 - t) * (1 - t) * sy + 2 * (1 - t) * t * cy + t * t * ey,
				});
			}
			shapes.fibres.push({type: 'line', points, width: 1.4, color: PALETTE.fibreDark});
			shapes.fibresTop.push({type: 'line', points, width: 0.65, color: PALETTE.fibre});
		}
	}
};

export const lumps = (shapes, run, random) => {
	const count = Math.round(polylineLength(run) / 10);
	for (let i = 0; i < count; i++) {
		const p = pointAlong(run, 0.1 + random() * 0.8);
		const rx = 2 + random() * 2.6;
		const ry = rx * (0.45 + random() * 0.25);
		const push = ry * (0.15 + random() * 0.45);
		const rotation = Math.atan2(p.ty, p.tx);
		shapes.lumps.push({type: 'ellipse', x: p.x + p.ty * push, y: p.y - p.tx * push, rx, ry, rotation, fill: PALETTE.meat, stroke: PALETTE.meatDark, strokeWidth: 0.7});
		shapes.lumps.push({type: 'ellipse', x: p.x + p.ty * push - p.tx * rx * 0.25, y: p.y - p.tx * push - p.ty * rx * 0.25, rx: rx * 0.4, ry: ry * 0.35, rotation, fill: PALETTE.meatBright});
	}
};

export const blotches = (shapes, run, random, depth, density, brain) => {
	const length = polylineLength(run);
	const count = Math.round(length / 4.5 * density);
	for (let i = 0; i < count; i++) {
		const p = pointAlong(run, random());
		const q = inward(p, 0.8 + random() * depth * 0.8);
		const roll = random();
		const fill = roll < 0.55 ? PALETTE.meatBright : roll < 0.85 ? PALETTE.meatDark : PALETTE.meatPit;
		const rx = 1.6 + random() * 2.8;
		shapes.band.push({type: 'ellipse', x: q.x, y: q.y, rx, ry: rx * (0.35 + random() * 0.3), rotation: Math.atan2(p.ty, p.tx) + (random() - 0.5) * 0.5, fill});
	}
	if (!brain) return;
	const brains = Math.max(1, Math.round(length / 16 * density));
	for (let i = 0; i < brains; i++) {
		const p = pointAlong(run, 0.15 + random() * 0.7);
		const q = inward(p, depth * (0.3 + random() * 0.3));
		const rx = 2.6 + random() * 2.2;
		const rotation = Math.atan2(p.ty, p.tx);
		shapes.band.push({type: 'ellipse', x: q.x, y: q.y, rx, ry: rx * 0.62, rotation, fill: PALETTE.fibre, stroke: PALETTE.fibreDark, strokeWidth: 0.8});
		shapes.band.push({type: 'line', points: [
			{x: q.x - p.tx * rx * 0.5, y: q.y - p.ty * rx * 0.5},
			{x: q.x - p.tx * rx * 0.1 + p.ty * 0.8, y: q.y - p.ty * rx * 0.1 - p.tx * 0.8},
			{x: q.x + p.tx * rx * 0.3, y: q.y + p.ty * rx * 0.3},
		], width: 0.7, color: PALETTE.fibreDark});
	}
};

export const stains = (shapes, art, run, random, blood, density, smear) => {
	const length = polylineLength(run);
	const points = run.map((p, i) => {
		const a = run[Math.max(0, i - 1)];
		const b = run[Math.min(run.length - 1, i + 1)];
		const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
		const depth = 0.6 + random() * 1.6;
		return {x: p.x - (b.y - a.y) / l * depth, y: p.y + (b.x - a.x) / l * depth};
	});
	shapes.stains.push({type: 'line', points, width: smear * (1 + random() * 0.5), color: blood});
	const runs = Math.round(length / 7 * density);
	for (let i = 0; i < runs; i++) {
		const p = pointAlong(run, 0.05 + random() * 0.9);
		const end = inward(p, 1.5 + Math.pow(random(), 1.5) * 6);
		const width = 1.3 + random() * 1.6;
		shapes.stains.push({type: 'line', points: [inward(p, 0.5), {x: (p.x + end.x) / 2 + (random() - 0.5), y: (p.y + end.y) / 2 + (random() - 0.5)}, end], width, color: blood});
		if (random() < 0.5) shapes.stains.push({type: 'circle', x: end.x, y: end.y, r: width * 0.7, fill: blood});
	}
	const drops = Math.round(length / 9 * density);
	for (let i = 0; i < drops; i++) {
		const p = inward(pointAlong(run, random()), 3 + random() * 6);
		if (art.alphaAt(p.x, p.y) < 0.5) continue;
		shapes.stains.push({type: 'circle', x: p.x, y: p.y, r: 0.6 + random() * 0.9, fill: random() < 0.7 ? blood : PALETTE.meat});
	}
};

export const speckles = (shapes, art, inner, run, random, blood, density) => {
	const count = Math.round(polylineLength(run) / 2.4 * density);
	for (let i = 0; i < count; i++) {
		const q = inward(pointAlong(run, random()), 2 + Math.pow(random(), 1.5) * 12);
		if (art.alphaAt(q.x, q.y) < 0.9 || !pointInPolygon(inner, q.x, q.y)) continue;
		const roll = random();
		shapes.speckles.push({type: 'circle', x: q.x, y: q.y, r: 0.4 + random() * 0.7, fill: roll < 0.6 ? blood : roll < 0.9 ? PALETTE.meat : PALETTE.meatDark});
	}
};

export const boneWidth = bone => Math.max(3, Math.min(7, bone.thickness * 0.27));

export const bar = (from, to, width) => {
	const dx = to.x - from.x;
	const dy = to.y - from.y;
	const length = Math.hypot(dx, dy) || 1;
	const px = -dy / length * width / 2;
	const py = dx / length * width / 2;
	return [
		{x: from.x - px, y: from.y - py}, {x: from.x + px, y: from.y + py},
		{x: to.x + px, y: to.y + py}, {x: to.x - px, y: to.y - py},
	];
};

export const brokenBone = (base, tip, width, random) => {
	const dx = tip.x - base.x;
	const dy = tip.y - base.y;
	const length = Math.hypot(dx, dy) || 1;
	const ux = dx / length, uy = dy / length;
	const px = -uy * width / 2, py = ux * width / 2;
	const a = 0.6 + random() * 1.8;
	const b = 0.4 + random() * 1.6;
	return [
		{x: base.x - px, y: base.y - py},
		{x: base.x + px, y: base.y + py},
		{x: tip.x + px - ux * a, y: tip.y + py - uy * a},
		{x: tip.x + px * 0.2 + ux * 0.4, y: tip.y + py * 0.2 + uy * 0.4},
		{x: tip.x - px * 0.35 - ux * b, y: tip.y - py * 0.35 - uy * b},
		{x: tip.x - px, y: tip.y - py},
	];
};

export const ribs = (shapes, art, run, random, rim, clean) => {
	for (let i = 1; i < run.length; i++) {
		const a = run[i - 1];
		const b = run[i];
		for (let row = art.h * 0.24; row <= art.h * 0.7; row += 7.5) {
			if ((a.y - row) * (b.y - row) > 0 || a.y === b.y) continue;
			const t = (row - a.y) / (b.y - a.y);
			const p = {x: a.x + (b.x - a.x) * t, y: row};
			const outX = (b.y - a.y) / Math.hypot(b.x - a.x, b.y - a.y);
			if (Math.abs(outX) < 0.4 || art.alphaAt(p.x, p.y) < 0.9) continue;
			const way = outX > 0 ? 1 : -1;
			const inner = {x: p.x - way * rim * (0.55 + random() * 0.3), y: p.y + (random() - 0.5)};
			if (art.alphaAt(inner.x, inner.y) < 0.9) continue;
			const points = clean ? bar(inner, p, 2.4) : brokenBone(inner, {x: p.x + way * (1.2 + random() * 3.2), y: p.y + (random() - 0.5) * 1.5}, 2.6 + random() * 0.8, random);
			shapes.bone.push({type: 'poly', points, fill: PALETTE.bone, stroke: PALETTE.boneLine, strokeWidth: 0.7});
		}
	}
};

export const guts = (shapes, run, random, rim) => {
	const count = Math.max(1, Math.round(polylineLength(run) / 14));
	for (let i = 0; i < count; i++) {
		const p = pointAlong(run, 0.15 + random() * 0.7);
		const q = inward(p, rim * (0.2 + random() * 0.4));
		const rx = 2.4 + random() * 2.2;
		const rotation = Math.atan2(p.ty, p.tx) + (random() - 0.5) * 0.9;
		shapes.lumps.push({type: 'ellipse', x: q.x, y: q.y, rx, ry: rx * 0.55, rotation, fill: PALETTE.gut, stroke: PALETTE.gutDark, strokeWidth: 0.8});
		shapes.lumps.push({type: 'ellipse', x: q.x - p.tx * rx * 0.25, y: q.y - p.ty * rx * 0.25, rx: rx * 0.35, ry: rx * 0.14, rotation, fill: PALETTE.gutSheen});
	}
};

export const skullEdge = (shapes, run) => {
	shapes.bandTop.push({type: 'line', points: run, width: 3.6, color: PALETTE.boneLine});
	shapes.bandTop.push({type: 'line', points: run, width: 2.4, color: PALETTE.bone});
};
